import React from 'react';
import { Field, FieldArray, CccisdInput, CccisdSelect, CccisdWysiwyg } from 'cccisd-formik';
import ButtonGroup from './ButtonGroup.js';
import FormulaPicker from './FormulaPicker.js';
import SurveyPicker from './SurveyPicker.js';
import { reportTemplateBuilder, BuilderBlock } from 'cccisd-laravel-appdefs';
import IconPlus from 'cccisd-icons/plus2';
import IconCross from 'cccisd-icons/cross4';
import classnames from 'classnames';
import notification from 'cccisd-notification';
import style from './style.css';

const initialFormulaValues = {
    label: '',
    variable: null,
    variable1: null,
    variable2: null,
};
const initialMarkerValues = {
    label: '',
    value: 0,
};
const initialExplanationValues = {
    conditionVariable: '',
    conditionMinValue: '',
    conditionMaxValue: '',
    text: '',
};
const initialPageValues = {
    title: '',
    subtitle: '',
    textAbove: '',
    textBelow: '',
    formulas: [{ id: 1, ...initialFormulaValues }],
    markers: [],
    explanations: [],
    yAxisLabel: '',
    min: 0,
    max: 5,
};
export const initialValues = {
    previewUserGroup: 'student',
    previewTimepoints: 2,
    surveyIds: [0],
    pages: [{ id: 1, ...initialPageValues }],
};
const userGroupOptions = [
    { value: 'student', label: 'Student' },
    { value: 'educator', label: 'Educator' },
    { value: 'provider', label: 'Provider' },
    { value: 'caregiver', label: 'Caregiver' },
];
const timelineOptions = [
    { value: 1, label: '1 timepoint' },
    { value: 2, label: '2 timepoints' },
    { value: 3, label: '3 timepoints' },
];

const getNewId = (arr = []) => Math.max(0, ...arr.map(item => item.id)) + 1;

const Component = props => {
    const renderSurveys = () => {
        return (
            <FieldArray name="surveyIds">
                {({ remove, push, form: { values } }) => {
                    if (!values.surveyIds) {
                        return null;
                    }
                    const { surveyIds } = values;

                    return (
                        <div>
                            <div>
                                <label className="form-label">Related surveys</label>
                            </div>
                            <div className={classnames(style.surveys, surveyIds.length <= 1 && style.oneSurvey)}>
                                {surveyIds.map((id, index) => (
                                    <React.Fragment key={index}>
                                        <Field name={`surveyIds.${index}`} component={SurveyPicker} />
                                        {surveyIds.length > 1 && (
                                            <div
                                                className={style.clickable}
                                                onClick={() => {
                                                    remove(index);
                                                }}
                                            >
                                                <span className="text-danger">
                                                    <IconCross />
                                                </span>
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                            <div className="mt-2 mb-3">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => {
                                        if (surveyIds.length >= 3) {
                                            notification({
                                                message: 'Maximum 3 surveys',
                                                type: 'danger',
                                            });
                                        } else {
                                            push(0);
                                        }
                                    }}
                                >
                                    <IconPlus spaceRight />
                                    Add survey
                                </button>
                            </div>
                        </div>
                    );
                }}
            </FieldArray>
        );
    };
    const renderFormulas = (page, index) => {
        const initialAssignmentId =
            Number(page.formulas.find(item => Number(item.variable?.assignmentId))?.variable.assignmentId) || 0;

        return (
            <FieldArray name={`pages.${index}.formulas`}>
                {({ remove, push, form: { values } }) => (
                    <div>
                        <h4 className="fw-bolder">Formulas</h4>
                        <div className="mb-2">
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => push({ id: getNewId(page.formulas), ...initialFormulaValues })}
                            >
                                <IconPlus spaceRight />
                                Add Formula
                            </button>
                        </div>
                        <table className={classnames('table table-borderless table-sm', style.formulasTable)}>
                            <thead>
                                <tr>
                                    <th>Label</th>
                                    {values.surveyIds.map((_, index2) => (
                                        <th key={index2}>Formula {index2 + 1}</th>
                                    ))}
                                    {page.formulas.length > 1 && <th />}
                                </tr>
                            </thead>
                            <tbody>
                                {page.formulas.map((formula, index1) => (
                                    <tr key={formula.id}>
                                        <td>
                                            <Field
                                                name={`pages.${index}.formulas.${index1}.label`}
                                                component={CccisdInput}
                                                wrapperClassName=""
                                            />
                                        </td>
                                        {values.surveyIds.map((_, index2) => (
                                            <td key={index2}>
                                                <Field
                                                    name={`pages.${index}.formulas.${index1}.variable${index2 || ''}`}
                                                    component={FormulaPicker}
                                                    wrapperClassName=""
                                                    disabled
                                                    initialAssignmentId={initialAssignmentId}
                                                />
                                            </td>
                                        ))}
                                        {page.formulas.length > 1 && (
                                            <td
                                                className={style.clickable}
                                                onClick={() => {
                                                    if (
                                                        page.explanations &&
                                                        page.explanations.some(
                                                            item =>
                                                                Number(item.conditionVariable) ===
                                                                formula.variable?.variableId
                                                        )
                                                    ) {
                                                        notification({
                                                            message: 'This formula is used in Conditional blocks.',
                                                            type: 'danger',
                                                        });
                                                        return;
                                                    }

                                                    remove(index1);
                                                }}
                                            >
                                                <span className="text-danger">
                                                    <IconCross />
                                                </span>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </FieldArray>
        );
    };
    const renderMarkers = (page, index) => (
        <FieldArray name={`pages.${index}.markers`}>
            {({ remove, push }) => (
                <div>
                    <h4 className="fw-bolder">Markers</h4>
                    <div className="mb-2">
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => push({ id: getNewId(page.markers), ...initialMarkerValues })}
                        >
                            <IconPlus spaceRight />
                            Add Marker
                        </button>
                    </div>
                    {page.markers.length === 0 ? (
                        <div>No markers yet</div>
                    ) : (
                        <div className={style.markers}>
                            <div className="fw-bold">Label</div>
                            <div className="fw-bold">Value</div>
                            <div>&nbsp;</div>
                            {page.markers.map((marker, index1) => (
                                <React.Fragment key={marker.id}>
                                    <div>
                                        <Field
                                            name={`pages.${index}.markers.${index1}.label`}
                                            component={CccisdInput}
                                            wrapperClassName=""
                                        />
                                    </div>
                                    <div>
                                        <Field
                                            name={`pages.${index}.markers.${index1}.value`}
                                            component={CccisdInput}
                                            wrapperClassName=""
                                            type="number"
                                        />
                                    </div>
                                    <div className={style.clickable} onClick={() => remove(index1)}>
                                        <span className="text-danger">
                                            <IconCross />
                                        </span>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </FieldArray>
    );
    const renderExplanations = (page, index) => {
        const formulasWithVariables = page.formulas.filter(formula => formula.variable?.label);

        const variableSubstituteExplanation = [
            '%studentName%',
            '%reporterName%',
            ...formulasWithVariables.map(formula => `%${formula.variable.label}%`),
        ].join(', ');
        const conditionOptions = [
            { value: '', label: 'Always show' },
            ...formulasWithVariables.map(formula => ({
                value: formula.variable.variableId,
                label: formula.variable.label,
            })),
        ];

        return (
            <FieldArray name={`pages.${index}.explanations`}>
                {({ remove, push }) => (
                    <div>
                        <h4 className="fw-bolder">Conditional blocks</h4>
                        <div className="mb-2">
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => push({ id: getNewId(page.explanations), ...initialExplanationValues })}
                            >
                                <IconPlus spaceRight />
                                Add Block
                            </button>
                        </div>
                        {!page.explanations || page.explanations.length === 0 ? (
                            <div>No blocks yet</div>
                        ) : (
                            <div className={style.explanations}>
                                {page.explanations.map((explanation, index1) => (
                                    <div key={explanation.id}>
                                        <div className="d-flex gap-2 align-items-center mb-1">
                                            <h5 className="fw-bolder mt-0 mb-0">Block {index1 + 1}</h5>
                                            <div className={style.clickable} onClick={() => remove(index1)}>
                                                <span className="text-danger">
                                                    <IconCross />
                                                </span>
                                            </div>
                                        </div>
                                        <Field
                                            name={`pages.${index}.explanations.${index1}.text`}
                                            description={
                                                variableSubstituteExplanation ? (
                                                    <div>
                                                        You can use these variables: {variableSubstituteExplanation}
                                                    </div>
                                                ) : null
                                            }
                                            component={CccisdWysiwyg}
                                            inputStyle={{ minHeight: '5em' }}
                                        />
                                        <div className="d-flex gap-2">
                                            <div style={{ width: '50%' }}>
                                                <Field
                                                    name={`pages.${index}.explanations.${index1}.conditionVariable`}
                                                    component={CccisdSelect}
                                                    options={conditionOptions}
                                                    label="Condition"
                                                />
                                            </div>
                                            {explanation.conditionVariable ? (
                                                <>
                                                    <Field
                                                        name={`pages.${index}.explanations.${index1}.conditionMinValue`}
                                                        component={CccisdInput}
                                                        type="number"
                                                        label="Min"
                                                        style={{ width: '5rem' }}
                                                    />
                                                    <Field
                                                        name={`pages.${index}.explanations.${index1}.conditionMaxValue`}
                                                        component={CccisdInput}
                                                        type="number"
                                                        label="Max"
                                                        style={{ width: '5rem' }}
                                                    />
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </FieldArray>
        );
    };

    return (
        <div>
            <FieldArray name="pages">
                {({ remove, push, form: { values } }) => {
                    const pages = values.pages || [];

                    return (
                        <div>
                            <Field
                                name="previewUserGroup"
                                component={ButtonGroup}
                                label="User group for chart preview"
                                options={userGroupOptions}
                            />
                            <Field
                                name="previewTimepoints"
                                component={ButtonGroup}
                                label="Timepoints for chart preview"
                                options={timelineOptions}
                            />
                            {renderSurveys()}
                            <h4 className="fw-bolder">Pages</h4>
                            {pages.length > 0 && (
                                <div className="mb-3">
                                    {pages.map((page, index) => (
                                        <BuilderBlock key={page.id} title={`Page ${index + 1}: ${page.title}`}>
                                            <div className="mb-3">
                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => remove(index)}
                                                >
                                                    <IconCross spaceRight />
                                                    Remove page
                                                </button>
                                            </div>
                                            <Field
                                                name={`pages.${index}.title`}
                                                component={CccisdInput}
                                                label="Title"
                                            />
                                            <Field
                                                name={`pages.${index}.subtitle`}
                                                component={CccisdInput}
                                                label="Subtitle"
                                            />
                                            <div className="d-flex gap-2">
                                                <Field
                                                    wrapperClassName=""
                                                    name={`pages.${index}.min`}
                                                    component={CccisdInput}
                                                    type="number"
                                                    label="Min value"
                                                    style={{ width: '5rem' }}
                                                />
                                                <Field
                                                    wrapperClassName=""
                                                    name={`pages.${index}.max`}
                                                    component={CccisdInput}
                                                    type="number"
                                                    label="Max value"
                                                    style={{ width: '5rem' }}
                                                />
                                            </div>
                                            <div className={style.separator} />
                                            {renderFormulas(page, index)}
                                            <div className={style.separator} />
                                            {renderMarkers(page, index)}
                                            <div className={style.separator} />
                                            {renderExplanations(page, index)}
                                            <div className={style.separator} />
                                            <Field
                                                name={`pages.${index}.yAxisLabel`}
                                                component={CccisdInput}
                                                label="Y Axis Label"
                                            />
                                            <Field
                                                name={`pages.${index}.textAbove`}
                                                component={CccisdWysiwyg}
                                                label="Text above the chart"
                                            />
                                            <Field
                                                wrapperClassName=""
                                                name={`pages.${index}.textBelow`}
                                                component={CccisdWysiwyg}
                                                label="Overview"
                                            />
                                        </BuilderBlock>
                                    ))}
                                </div>
                            )}
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => push({ id: getNewId(pages), ...initialPageValues })}
                            >
                                Add Page
                            </button>
                        </div>
                    );
                }}
            </FieldArray>
        </div>
    );
};

export default reportTemplateBuilder({})(Component);
